import React from 'react'

import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import {
  ABSENT_NOTE_MESSAGE,
  BADGE_CONFIG_BY_STATE,
  WIB_SUFFIX,
} from 'app/KinerjaHarian/constants'
import { KinerjaHarianNoteCard } from 'app/KinerjaHarian/components/logs/KinerjaHarianNoteCard'
import { KinerjaHarianStatusBadge } from 'app/KinerjaHarian/components/logs/KinerjaHarianStatusBadge'
import { useKinerjaHarianTrackEvent } from 'app/KinerjaHarian/hooks/useKinerjaHarianTrackEvent'

import type { KinerjaHarianLog, KinerjaHarianNote } from 'types/kinerja-harian'

function formatDateLabel(entryDate: string) {
  const [year, month, day] = entryDate.split('-').map(Number)
  const formattedDate = new Date(year, month - 1, day).toLocaleDateString(
    'id-ID',
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }
  )

  return formattedDate.replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatHourLabel(hour?: string) {
  if (!hour) {
    return '-'
  }

  return `${hour} ${WIB_SUFFIX}`
}

interface KinerjaHarianCalendarDetailPopupProps {
  isOpen: boolean
  item: KinerjaHarianLog | null
  isEditDisabled: boolean
  onClose: () => void
  onEdit: (itemId: string) => void
}

export function KinerjaHarianCalendarDetailPopup({
  isOpen,
  item,
  isEditDisabled,
  onClose,
  onEdit,
}: KinerjaHarianCalendarDetailPopupProps) {
  const { trackEvent } = useKinerjaHarianTrackEvent()

  if (!item) {
    return null
  }

  const dateLabel = formatDateLabel(item.entryDate)
  const badgeConfig =
    BADGE_CONFIG_BY_STATE?.[item.state] ?? BADGE_CONFIG_BY_STATE['SUBMITTED']
  const notes: KinerjaHarianNote[] = item.contents.map((content, index) => ({
    id: content.contentId,
    activity: content.contentDescription || `Konten ${index + 1}`,
    startTime: formatHourLabel(content.startHour),
    endTime: formatHourLabel(content.endHour),
  }))

  return (
    <Dialog isOpen={isOpen} onClose={onClose} autoFocus={false}>
      <DialogOverlay />
      <DialogContent>
        <DialogHeader
          borderless
          title="Rincian Kinerja Harian"
          className="!bg-white"
        />
        <DialogCloseButton
          onClick={() => {
            trackEvent({
              activityType: 'click_popup_edit',
              screenName: 'harian',
              additionalParams: {
                clicked_text: 'x',
              },
            })
            onClose()
          }}
        />

        <div className="px-6 py-2 space-y-4">
          <Text className="pb-4 text-gray-70">{dateLabel}</Text>

          <div className="flex items-center justify-between gap-3 pb-2">
            <div className="flex items-center gap-2">
              <Icon color="informational">calendar_today</Icon>
              <Text>Status:</Text>
            </div>
            <KinerjaHarianStatusBadge tone={badgeConfig.tone}>
              {badgeConfig.label}
            </KinerjaHarianStatusBadge>
          </div>

          <Text variant="body-bold">Catatan Kinerja:</Text>

          <div className="space-y-3 overflow-y-auto">
            {item.absent && (
              <div className="px-4 py-5 border rounded-lg border-red-40 bg-surface-critical-subdued">
                <Text color="critical">{ABSENT_NOTE_MESSAGE}</Text>
              </div>
            )}

            {!item.absent && (
              <>
                {notes.length > 0 ? (
                  notes.map((note) => (
                    <KinerjaHarianNoteCard key={note.id} note={note} />
                  ))
                ) : (
                  <div className="px-4 py-5 border border-dashed rounded-lg border-gray-30 bg-gray-10">
                    <Text color="subdued">
                      Belum ada catatan kinerja untuk tanggal ini.
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-around gap-3 px-6 py-4">
          <Button
            color="black"
            fullWidth
            onClick={() => {
              trackEvent({
                activityType: 'click_popup_edit',
                screenName: 'harian',
                additionalParams: {
                  clicked_text: 'Kembali',
                },
              })
              onClose()
            }}
            variant="ghost"
          >
            Kembali
          </Button>

          {!isEditDisabled && (
            <Button
              color="blue"
              className="space-x-1"
              disabled={isEditDisabled}
              fullWidth
              onClick={() => {
                trackEvent({
                  activityType: 'click_popup_edit',
                  screenName: 'harian',
                  additionalParams: {
                    clicked_text: 'Ubah Catatan',
                  },
                })
                onClose()
                onEdit(item.entryDate)
              }}
            >
              <span className="flex items-center gap-1">
                <Icon
                  fontSize="sm"
                  color={isEditDisabled ? 'disabled' : 'inverse'}
                >
                  edit
                </Icon>
                Ubah Catatan
              </span>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
